import React, { useState, useEffect, Fragment } from 'react';
import T from 'prop-types';
// components
import Link from 'react-router/lib/Link';
import TreatmentProductInfo from './TreatmentProductInfo';
import Translation from 'components/Translation';
import { FormattedMessage } from 'react-intl';
import Waypoint from 'components/Waypoint/Waypoint';
import InfoRow from 'components/InfoRow/InfoRow';
import DotSeparator from 'components/DotSeparator/DotSeparator';
import Collapse from 'react-collapse';
import Button from 'components/Button';
import { CombinedBadge } from 'components/StatusBadge/StatusBadge';
// utils
import cn from 'classnames';
import moment from 'moment';
// regional date handling
import { connect } from 'react-redux';
import { getCurrentCountryCode } from 'selectors/regionalDateSelectors';
import { formatDateForUser, formatDateRangeForUser, formatDateTimeForUser } from 'utils/regionalDateHelper';
// styles
import styles from './TreatmentStepCard.module.scss';

const formatDate = (date, countryCode) => formatDateForUser(date, countryCode);
const limitText = (text, limit) => (text.length > limit ? text.substring(0, limit) + '...' : text);

const TreatmentStepCard = ({
  className,
  hasNoDetails = false,
  pigGroup,
  treatmentStepData,
  onReport,
  reportSteps = false,
  asOf, // day the schedule is judged against (a barn-sheet correction's date); today by default
  getProtocolLink,
  countryCode
}) => {
  const [isOpened, setIsOpened] = useState(false);
  const [isDetailsOpened, setIsDetailsOpened] = useState(false);
  const {
    treatment_protocol_product: treatmentProtocolProduct,
    start_date: startDate, // date when treatment starts (start window opens for flexible)
    end_date: endDate, // date when treatment ends (start window closes for flexible)
    step_type: stepCondition, // possible values: [before, between, after, on, flexible]
    used_at: treatmentUsageDate,
    anchor_date: anchorDate, // flexible: date of the first administration (schedule anchor)
    consecutive_duration: consecutiveDuration, // flexible: number of consecutive days the course runs
    repeat,
    combined_protocols: combinedProtocols, // copies of several sow units sharing this treatment
  } = treatmentStepData;
  const isFlexible = stepCondition === 'flexible';
  const now = asOf ? moment(asOf) : moment();
  // Flexible: before the first administration use the start window; after, the anchored N-day schedule
  const scheduleStart = isFlexible ? (anchorDate || startDate) : startDate;
  const scheduleEnd = isFlexible && anchorDate && consecutiveDuration ?
    moment(anchorDate).add(consecutiveDuration - 1, 'days') :
    endDate;

  const { treatment_product, treatment_protocol } = treatmentProtocolProduct;
  const treatmentProtocolLink = getProtocolLink ? getProtocolLink(treatmentProtocolProduct) : null;
  const treatmentProtocolName = combinedProtocols
    ? [...new Set(combinedProtocols.map(({ name }) => name).filter(Boolean))].sort().join(' / ')
    : treatment_protocol.name;
  const renderProtocolLink = (path, name) => (
    <Link className="primary" to={path} target="_blank" rel="noopener noreferrer">{name}</Link>
  );

  // Each copy of a combined line links to its own profile; a name without a link stays plain text
  const renderProtocolName = () => {
    if (!treatmentProtocolLink) return treatmentProtocolName;
    if (!combinedProtocols) return renderProtocolLink(treatmentProtocolLink, treatmentProtocolName);

    return combinedProtocols.map((protocol, index) => {
      const path = getProtocolLink({ treatment_protocol: protocol });
      return (
        <Fragment key={protocol.id}>
          {index > 0 && ' / '}
          {path ? renderProtocolLink(path, protocol.name) : protocol.name}
        </Fragment>
      );
    });
  };
  const sourceFarmName = combinedProtocols
    ? combinedProtocols.map(({ source_farm }) => source_farm?.name).filter(Boolean).join(', ')
    : treatment_protocol?.farm_treatment_protocol?.farm?.name;
  const formattedStartDate = formatDate(scheduleStart, countryCode);
  const formattedEndDate = formatDate(scheduleEnd, countryCode);
  // Until the anchor a flexible step can start on any window day, so the timeline shows the window
  const formattedStartRange = isFlexible && !anchorDate ?
    formatDateRangeForUser(startDate, endDate, countryCode) :
    formattedStartDate;
  // and end on any window day shifted by the run length
  const formattedEndRange = isFlexible && !anchorDate && consecutiveDuration ?
    formatDateRangeForUser(
      moment(startDate).add(consecutiveDuration - 1, 'days'),
      moment(endDate).add(consecutiveDuration - 1, 'days'),
      countryCode
    ) :
    formattedEndDate;
  // Multi-placement protocol instances arrive on their own placement date, not the group start
  const pigsArrivalDate = formatDateForUser(treatment_protocol?.placement_date || pigGroup.started_on, countryCode);
  const runCount = isFlexible ?
    consecutiveDuration :
    moment(endDate).diff(moment(startDate), 'days') + 1; // taking current day into account
  const isOutdated = now.diff(moment(scheduleEnd), 'days') > 0; // schedule end is before the judged day
  const legacyExpired = repeat ?
    isOutdated && (!treatmentUsageDate ||
      moment(endDate).startOf('day').isAfter(moment(treatmentUsageDate).startOf('day'))) :
    isOutdated && !treatmentUsageDate;
  // Daily: given on the last window day closes the course, but not when judging an earlier day
  const legacyCompleted = repeat ?
    (treatmentUsageDate && moment(treatmentUsageDate).startOf('day').isSameOrAfter(moment(endDate).startOf('day')) &&
      !now.isBefore(moment(endDate), 'day')) :
    !!treatmentUsageDate;
  // Flexible: expired only if the window closed before any administration (anchored courses keep running);
  // completed once anchored and the whole consecutive schedule has elapsed
  const isTreatmentExpired = isFlexible ? (!anchorDate && isOutdated) : legacyExpired;
  const isTreatmentCompleted = isFlexible ?
    (!!anchorDate && now.clone().startOf('day').isAfter(moment(scheduleEnd).startOf('day'))) :
    legacyCompleted;
  // Flexible is future only until its start window opens (once anchored it is never future)
  const isFutureTreatment = isFlexible ?
    (!anchorDate && moment(startDate).isAfter(now, 'day')) :
    moment(startDate).isAfter(now);

  useEffect(() => {
    if (reportSteps && onReport && !(isTreatmentCompleted || isFutureTreatment)) {
      onReport(treatmentStepData);
    }
  }, [onReport, isTreatmentCompleted, isFutureTreatment, treatmentStepData, reportSteps]);


  const toggleCollapse = () => setIsOpened(!isOpened);
  const toggleDetailsCollapse = () => setIsDetailsOpened(!isDetailsOpened);

  // Flexible: the footer names the start window until the anchor, the locked run after it
  const getFooterLabelKey = () => {
    if (isFlexible && anchorDate) return 'general.runTreatmentForPigs.flexibleAnchored';
    return `general.runTreatmentForPigs.${stepCondition}`;
  };

  const getFooterLabelValues = () => {
    return { start_date: formattedStartDate, end_date: formattedEndDate, consecutive_duration: consecutiveDuration };
  };

  const renderTreatmentTimeLine = () => (
    <div className={styles['treatment-timeline']}>
      <Waypoint type="source">
        <div className="semibold">
          <InfoRow
            label={<FormattedMessage id="container.checkupIndex.row.start" />}
            value={scheduleStart ? formattedStartRange : <FormattedMessage id="general.notAvail" />}
          />
        </div>
        <div className={styles['duration-wrapper']}>
          {stepCondition !== 'after' && !repeat && !!startDate && (
            <div className={styles.duration}>
              <FormattedMessage
                id={stepCondition === 'between' ? 'general.runBetweenDaysCount' : 'general.runDaysCount'}
                values={{ days_count: <FormattedMessage id="general.daysCount" values={{ count: runCount }} /> }}
              />
            </div>
          )}
          {stepCondition === 'between' && repeat && (
            <div className={styles.duration}>
              <FormattedMessage
                id="general.runEverydayInDaysCount"
                values={{ days_count: <FormattedMessage id="general.daysCount" values={{ count: runCount }} /> }}
              />
            </div>
          )}
        </div>
      </Waypoint>
      <Waypoint icon={<i className={cn('fa fa-times', styles['end-icon'])} />} isEnd>
        <div className="semibold">
          <InfoRow
            label={<FormattedMessage id={isFlexible ? 'general.treatmentEnds' : 'general.treatmentWindowEnds'} />}
            value={endDate ? formattedEndRange : <FormattedMessage id="general.notAvail" />}
          />
        </div>
      </Waypoint>
    </div>
  );

  return (
    <div className={cn(styles['treatment-step-card'], className)}>
      <div
        className={cn(styles['status-line'], {
          [styles.completed]: isTreatmentCompleted,
          [styles.expired]: isTreatmentExpired,
        })}
      >
        <InfoRow
          label={<FormattedMessage id="general.treatment" />}
          value={(<FormattedMessage id="general.arrivingPigsOnDate" values={{ date: pigsArrivalDate }} />)}
        />
        {treatmentUsageDate && (
          <InfoRow
            className={styles['usage-row']}
            label={<FormattedMessage id="general.usedOn" />}
            value={formatDateTimeForUser(treatmentUsageDate, countryCode)}
          />
        )}

        {isTreatmentExpired && <div className={styles['expired-label']}><FormattedMessage id="general.expired" /></div>}
      </div>

      {/* HEADER */}
      <div className={styles['card-header']}>
        <i
          className={cn({
            [styles['complete-icon']]: isTreatmentCompleted,
            [styles['active-icon']]: !isTreatmentCompleted,
            'fa fa-check-circle-bts': isTreatmentCompleted,
          })}
        />
        <span className="semibold nowrap-ellipsis pl-20">
          {treatmentProtocolProduct.treatment_product.name}
          <DotSeparator />
          {renderProtocolName()}
        </span>
        {combinedProtocols && <CombinedBadge className={styles['combined-badge']} />}

        <div className={styles['btn-group']}>

          {isTreatmentCompleted && (
            <span className={styles['complete-label']}>
              <FormattedMessage
                id="general.completedDate"
                values={{ date: formatDate(treatmentUsageDate, countryCode) }}
              />
            </span>
          )}

          {onReport && !(isTreatmentCompleted || isFutureTreatment) && (
            <Button
              primary
              onClick={() => onReport(treatmentStepData)}
              className={cn(styles['submit-button'], { 'mr-0': hasNoDetails })}
            >
              <FormattedMessage id="general.report" />
            </Button>
          )}

          {!hasNoDetails && (
            <i
              onClick={toggleCollapse}
              className={cn('fa fa-angle-right', styles['collapse-icon'], { [styles.opened]: isOpened })}
            />
          )}
        </div>

      </div>

      {/* CONTENT */}
      {!hasNoDetails && (
        <Collapse isOpened={isOpened}>
          <div className={styles['collapse-content']}>
            <div className={styles['general-info']}>
              {renderTreatmentTimeLine()}
              <div className={styles['product-info']}>
                <div className="semibold">{treatment_product.name}</div>
                <InfoRow
                  label={<span className="semibold"><FormattedMessage id="general.headTreated" /></span>}
                  value={treatmentProtocolProduct.head_treated}
                />
                <InfoRow
                  label={<span className="semibold"><FormattedMessage id="general.sourceFarm" /></span>}
                  value={sourceFarmName || <FormattedMessage id="general.notAvail" />}
                />
              </div>
            </div>
            <TreatmentProductInfo treatmentProtocolProduct={treatmentProtocolProduct} />
          </div>
        </Collapse>
      )}

      <div className={styles['mobile-content']}>
        {!hasNoDetails && renderTreatmentTimeLine()}
        <div onClick={toggleDetailsCollapse} className={styles['treatment-details']}>
          <div className="semibold">
            <FormattedMessage id="general.treatmentDetails" />
          </div>
          <i className={cn('fa fa-angle-right', styles['collapse-icon'], { [styles.opened]: isDetailsOpened })} />
        </div>
        <Collapse isOpened={isDetailsOpened}>
          <div className={styles['notes-section']}>
            <div className={styles['notes-item']}>
              <div className="semibold">
                <FormattedMessage id="general.medicationInformation" />
              </div>
              {treatmentProtocolProduct.notes ? (
                <Fragment>
                  <Translation text={limitText(treatmentProtocolProduct.notes, 128)}>
                    <span>{limitText(treatmentProtocolProduct.notes, 128)}</span>
                  </Translation>
                  <div>
                    <Link
                      target="_blank"
                      className="link"
                      to={`/admin/health-variables/medications/${treatmentProtocolProduct.id}/information`}
                    >
                      <FormattedMessage id="general.readMore" />
                    </Link>
                  </div>
                </Fragment>
              ) : <FormattedMessage id="general.notAvail" />}
            </div>

            <div className={styles['notes-item']}>
              <div className="semibold mt-15">
                <FormattedMessage id="general.dosageNotes" />
              </div>

              {treatmentProtocolProduct.dosage_comment ? (
                <Translation text={treatmentProtocolProduct.dosage_comment}>
                  <span>{treatmentProtocolProduct.dosage_comment}</span>
                </Translation>
              ) : <FormattedMessage id="general.notAvail" />}
            </div>

            <div className={styles['notes-item']}>
              <div className="semibold mt-15">
                <FormattedMessage id="general.productNotes" />
              </div>
              {treatmentProtocolProduct.comment ? (
                <Translation text={treatmentProtocolProduct.comment}>
                  <span>{treatmentProtocolProduct.comment}</span>
                </Translation>
              ) : <FormattedMessage id="general.notAvail" />}
            </div>

            <div className={styles['notes-item']}>
              <div className="semibold mt-15">
                <FormattedMessage id="general.treatmentProtocolNotes" />
              </div>
              {treatmentProtocolProduct.treatment_protocol.comment ? (
                <Translation text={treatmentProtocolProduct.treatment_protocol.comment}>
                  <span>{treatmentProtocolProduct.treatment_protocol.comment}</span>
                </Translation>
              ) : <FormattedMessage id="general.notAvail" />}
            </div>
          </div>
        </Collapse>
        {onReport && !(isTreatmentCompleted || isFutureTreatment) && (
          <Button primary onClick={() => onReport(treatmentStepData)} className={cn(styles['submit-button'])}>
            <FormattedMessage id="general.report" />
          </Button>
        )}
      </div>

      {/* FOOTER */}
      <div className={cn(styles['card-footer'], { [styles.hidden]: isOpened })}>
        <InfoRow
          className="nowrap-ellipsis"
          label={<FormattedMessage id="general.treatmentDirections" />}
          value={(
            <FormattedMessage
              id={getFooterLabelKey()}
              values={{ ...getFooterLabelValues(), arrive_date: pigsArrivalDate, b: (msg) => <b>{msg}</b> }}
            />
          )}
        />
      </div>
    </div>
  );
};

TreatmentStepCard.propTypes = {
  className: T.string,
  hasNoDetails: T.bool,
  pigGroup: T.object.isRequired,
  treatmentStepData: T.object.isRequired,
  onReport: T.func,
  reportSteps: T.bool,
  asOf: T.string,
  getProtocolLink: T.func,
  countryCode: T.string,
};

export default connect(
  (state) => ({
    countryCode: getCurrentCountryCode(state),
  })
)(TreatmentStepCard);
